import React, { useState, useEffect } from 'react';
import { INVESTIGATION_TEMPLATES, MICROSCOPY_OPTIONS } from '../utils/microbiologyTemplateEngine';

export default function MicroscopySection({
  data,
  onSaveMicroscopy,
  onProceedToCulture
}) {
  const [gramReaction, setGramReaction] = useState('');
  const [morphology, setMorphology] = useState('');
  const [arrangement, setArrangement] = useState('');
  const [quantity, setQuantity] = useState('');
  const [pusCells, setPusCells] = useState(data?.pusCells || 'Nil');
  const [rbc, setRbc] = useState(data?.rbc || 'Nil');
  const [epithelialCells, setEpithelialCells] = useState(data?.epithelialCells || 'Nil');
  const [bacteria, setBacteria] = useState('Nil');
  const [otherFindings, setOtherFindings] = useState('');
  const [fullObservationText, setFullObservationText] = useState(
    data?.gramStain || data?.microscopyObservations || ''
  );
  const [saving, setSaving] = useState(false);

  // Identify investigation template from specimen
  const specimenName = (data?.specimenName || '').toLowerCase();
  let currentTemplate = INVESTIGATION_TEMPLATES.URINE;
  if (specimenName.includes('blood')) currentTemplate = INVESTIGATION_TEMPLATES.BLOOD;
  else if (specimenName.includes('sputum') || specimenName.includes('bal') || specimenName.includes('tracheal'))
    currentTemplate = INVESTIGATION_TEMPLATES.SPUTUM;
  else if (specimenName.includes('pus') || specimenName.includes('wound') || specimenName.includes('swab') || specimenName.includes('tissue'))
    currentTemplate = INVESTIGATION_TEMPLATES.PUS_SWAB;
  else if (specimenName.includes('stool') || specimenName.includes('feces'))
    currentTemplate = INVESTIGATION_TEMPLATES.STOOL;
  else if (specimenName.includes('fluid') || specimenName.includes('csf') || specimenName.includes('pleural') || specimenName.includes('synovial'))
    currentTemplate = INVESTIGATION_TEMPLATES.BODY_FLUID;

  useEffect(() => {
    if (data) {
      setPusCells(data.pusCells || 'Nil');
      setRbc(data.rbc || 'Nil');
      setEpithelialCells(data.epithelialCells || 'Nil');
      setFullObservationText(data.gramStain || data.microscopyObservations || '');
    }
  }, [data]);

  // Construct auto-generated structured summary
  const buildObservationText = (newGram, newMorph, newArr, newQty, newPus, newRbc, newEpi, newBact, extra) => {
    const parts = [];

    if (newGram && newGram !== 'No Organisms Seen' && newGram !== 'Not Seen') {
      let desc = `${newGram} ${newMorph || 'bacteria'}`;
      if (newArr) desc += ` in ${newArr.toLowerCase()}`;
      if (newQty) desc += ` (${newQty})`;
      parts.push(`${desc} seen on direct Gram smear.`);
    } else if (newGram === 'No Organisms Seen' || newGram === 'Not Seen') {
      parts.push('No microorganisms detected on direct Gram stained smear.');
    }

    const cellParts = [];
    if (newPus && newPus !== 'Nil') cellParts.push(`Pus cells: ${newPus}`);
    else cellParts.push('Pus cells: Nil');

    if (newRbc && newRbc !== 'Nil') cellParts.push(`RBCs: ${newRbc}`);
    if (newEpi && newEpi !== 'Nil') cellParts.push(`Epithelial cells: ${newEpi}`);
    if (newBact && newBact !== 'Nil') cellParts.push(`Bacteria: ${newBact}`);

    if (cellParts.length > 0) {
      parts.push(cellParts.join(', ') + '.');
    }

    if (extra && extra.trim()) {
      parts.push(extra.trim());
    }

    return parts.join(' ');
  };

  const handleApplyPreset = (presetId) => {
    const preset = currentTemplate.quickTemplates?.find((p) => p.id === presetId);
    if (!preset) return;
    setPusCells(preset.pusCells);
    setRbc(preset.rbc);
    setEpithelialCells(preset.epithelialCells);
    setFullObservationText(preset.gramStain);
  };

  const handleStructuredChange = (type, val) => {
    let nextGram = gramReaction;
    let nextMorph = morphology;
    let nextArr = arrangement;
    let nextQty = quantity;
    let nextPus = pusCells;
    let nextRbc = rbc;
    let nextEpi = epithelialCells;
    let nextBact = bacteria;

    if (type === 'gram') {
      nextGram = val;
      setGramReaction(val);
    } else if (type === 'morph') {
      nextMorph = val;
      setMorphology(val);
    } else if (type === 'arr') {
      nextArr = val;
      setArrangement(val);
    } else if (type === 'qty') {
      nextQty = val;
      setQuantity(val);
    } else if (type === 'pus') {
      nextPus = val;
      setPusCells(val);
    } else if (type === 'rbc') {
      nextRbc = val;
      setRbc(val);
    } else if (type === 'epi') {
      nextEpi = val;
      setEpithelialCells(val);
    } else if (type === 'bact') {
      nextBact = val;
      setBacteria(val);
    }

    const updatedText = buildObservationText(
      nextGram,
      nextMorph,
      nextArr,
      nextQty,
      nextPus,
      nextRbc,
      nextEpi,
      nextBact,
      otherFindings
    );
    setFullObservationText(updatedText);
  };

  const handleSave = async (isProceed = false) => {
    setSaving(true);
    try {
      await onSaveMicroscopy({
        cultureID: data.cultureID,
        gramStain: fullObservationText,
        pusCells: pusCells,
        epithelialCells: epithelialCells,
        rbc: rbc,
        additionalFindings: otherFindings
      });
      if (isProceed && onProceedToCulture) {
        onProceedToCulture();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wb-stage-view">
      {/* Step Header */}
      <div className="wb-stage-header">
        <div className="wb-stage-title-wrap">
          <h3 className="wb-stage-title">Direct Smear & Wet Mount Microscopy</h3>
          <span className="wb-stage-subtitle">
            Template: <strong>{currentTemplate.name}</strong> ({data?.specimenName})
          </span>
        </div>

        {/* Quick Clinical Preset Dropdown */}
        <div className="wb-stage-header-actions">
          <label className="wb-label-inline">Clinical Preset:</label>
          <select
            className="wb-select-compact"
            onChange={(e) => handleApplyPreset(e.target.value)}
            defaultValue=""
          >
            <option value="">-- Apply Preset Template --</option>
            {currentTemplate.quickTemplates?.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Clinical Form */}
      <div className="wb-clinical-form-layout">
        {/* Left Column: Organisms & Smear Characteristics */}
        <div className="wb-form-column">
          {/* 1. Gram Stain Reaction Segmented Bar */}
          <div className="wb-field-group">
            <label className="wb-field-label">
              Gram Stain Reaction <span className="wb-req">*</span>
            </label>
            <div className="wb-segmented-control">
              {[
                { value: 'Gram Positive', label: 'Gram Positive (+)' },
                { value: 'Gram Negative', label: 'Gram Negative (-)' },
                { value: 'Mixed', label: 'Mixed Flora' },
                { value: 'Variable', label: 'Variable' },
                { value: 'None', label: 'None Seen' }
              ].map((g) => (
                <button
                  key={g.value}
                  type="button"
                  className={`wb-seg-btn ${gramReaction === g.value ? 'active' : ''}`}
                  onClick={() => handleStructuredChange('gram', g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div className="wb-grid-2col">
            <div className="wb-field-group">
              <label className="wb-field-label">Morphology</label>
              <select
                className="wb-select"
                value={morphology}
                onChange={(e) => handleStructuredChange('morph', e.target.value)}
              >
                <option value="">-- Select Morphology --</option>
                {[
                  'Cocci',
                  'Bacilli',
                  'Coccobacilli',
                  'Diplococci',
                  'Yeast',
                  'Fungal Hyphae',
                  'Pleomorphic',
                  'Curved / Comma'
                ].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="wb-field-group">
              <label className="wb-field-label">Arrangement</label>
              <select
                className="wb-select"
                value={arrangement}
                onChange={(e) => handleStructuredChange('arr', e.target.value)}
              >
                <option value="">-- Select Arrangement --</option>
                {['Clusters', 'Chains', 'Pairs', 'Single', 'Palisading', 'Intracellular'].map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="wb-field-group">
            <label className="wb-field-label">Organism Load</label>
            <div className="wb-segmented-control">
              {[
                { value: 'Few (1+)', label: 'Few' },
                { value: 'Moderate (2+)', label: 'Moderate' },
                { value: 'Many (3+)', label: 'Many' },
                { value: 'Heavy (4+)', label: 'Heavy' }
              ].map((q) => (
                <button
                  key={q.value}
                  type="button"
                  className={`wb-seg-btn ${quantity === q.value ? 'active' : ''}`}
                  onClick={() => handleStructuredChange('qty', q.value)}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Host Inflammatory & Cellular Findings */}
        <div className="wb-form-column">
          <div className="wb-grid-3col">
            <div className="wb-field-group">
              <label className="wb-field-label">
                Pus Cells / WBC <span className="wb-req">*</span>
              </label>
              <select
                className="wb-select"
                value={pusCells}
                onChange={(e) => handleStructuredChange('pus', e.target.value)}
              >
                {MICROSCOPY_OPTIONS.wbcRanges.map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </div>

            <div className="wb-field-group">
              <label className="wb-field-label">RBCs</label>
              <select
                className="wb-select"
                value={rbc}
                onChange={(e) => handleStructuredChange('rbc', e.target.value)}
              >
                {MICROSCOPY_OPTIONS.rbcRanges.map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </div>

            <div className="wb-field-group">
              <label className="wb-field-label">Epithelial Cells</label>
              <select
                className="wb-select"
                value={epithelialCells}
                onChange={(e) => handleStructuredChange('epi', e.target.value)}
              >
                {MICROSCOPY_OPTIONS.epithelialRanges.map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="wb-field-group">
            <label className="wb-field-label">Additional Findings (Casts, Crystals, Motility)</label>
            <input
              type="text"
              className="wb-input"
              placeholder="e.g., Triple phosphate crystals, active darting motility, fungal elements..."
              value={otherFindings}
              onChange={(e) => {
                setOtherFindings(e.target.value);
                const updated = buildObservationText(
                  gramReaction,
                  morphology,
                  arrangement,
                  quantity,
                  pusCells,
                  rbc,
                  epithelialCells,
                  bacteria,
                  e.target.value
                );
                setFullObservationText(updated);
              }}
            />
          </div>
        </div>
      </div>

      {/* Generated Observation Statement */}
      <div className="wb-statement-box">
        <div className="wb-statement-header">
          <label className="wb-field-label" style={{ margin: 0 }}>
            Official Smear Observation Statement
          </label>
          <span className="wb-hint-text">
            Auto-compiled from structured entries &bull; Editable by microbiologist
          </span>
        </div>
        <textarea
          rows="3"
          className="wb-textarea"
          value={fullObservationText}
          onChange={(e) => setFullObservationText(e.target.value)}
          placeholder="Direct microscopic observations statement..."
        />
      </div>

      {/* In-Page Action Toolbar */}
      <div className="wb-stage-actions-bar">
        <button
          type="button"
          className="wb-btn-text"
          onClick={() => {
            setGramReaction('');
            setMorphology('');
            setArrangement('');
            setQuantity('');
            setPusCells('Nil');
            setRbc('Nil');
            setEpithelialCells('Nil');
            setOtherFindings('');
            setFullObservationText('');
          }}
        >
          Clear Selections
        </button>

        <div className="wb-actions-right">
          <button
            type="button"
            className="wb-btn-primary"
            disabled={saving}
            onClick={() => handleSave(false)}
          >
            {saving ? 'Saving...' : 'Save Observations'}
          </button>
        </div>
      </div>
    </div>
  );
}
